import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { uploadRolloverCsv } from '../../apis/rolloverUpload';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { RootState } from '../../redux/store';
import { RowErrorGroup } from './types';
import UploadCsvPage from './UploadCsvPage';
import { groupErrorsByRow } from './utils/groupErrors';

const ImportRollOverUsersContainer: React.FC = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [errorRows, setErrorRows] = useState<RowErrorGroup[] | null>(null);
  const [uploading, setUploading] = useState(false);

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleCloseToast = () => setToastOpen(false);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const response = await uploadRolloverCsv(file, loggedInUser.school);
      if (response.errors && response.errors.length > 0) {
        setErrorRows(groupErrorsByRow(response.errors));
        showToast('Upload completed with errors.', SnackbarSeverityType.error);
      } else {
        setErrorRows([]);
        showToast('File processed successfully!', SnackbarSeverityType.success);
      }
    } catch (err: any) {
      // backend might send same shape or generic error
      const errors = err?.errors;
      if (Array.isArray(errors) && errors.length > 0) {
        setErrorRows(groupErrorsByRow(errors));
        showToast(
          'Upload failed with unexpected errors.',
          SnackbarSeverityType.error
        );
      } else {
        setErrorRows(null);
        showToast(
          'Unexpected error uploading file.',
          SnackbarSeverityType.error
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const resetErrors = () => setErrorRows(null);

  return (
    <>
      <UploadCsvPage
        school={loggedInUser.school}
        onUpload={handleUpload}
        uploading={uploading}
        errorRows={errorRows}
        resetErrors={resetErrors}
      />
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default ImportRollOverUsersContainer;
